import { create } from "zustand"
import { persist } from "zustand/middleware"
import { createClient } from "@/lib/supabase/client"

export interface Comment {
  id: string
  user_id: string
  post_site: string
  post_id: string
  content: string
  parent_id: string | null
  is_edited: boolean
  is_deleted: boolean
  upvotes: number
  downvotes: number
  created_at: string
  updated_at: string
  // Joined data
  username?: string
  profile_picture_url?: string
}

interface CommentsState {
  comments: Record<string, Comment[]>
  isLoading: boolean
  fetchComments: (postSite: string, postId: string) => Promise<void>
  addComment: (postSite: string, postId: string, content: string, userId: string, parentId?: string) => Promise<boolean>
  editComment: (commentId: string, content: string) => Promise<boolean>
  deleteComment: (commentId: string) => Promise<boolean>
  voteComment: (commentId: string, voteType: "up" | "down") => Promise<boolean>
  getComments: (postSite: string, postId: string) => Comment[]
}

export const useCommentsStore = create<CommentsState>()(
  persist(
    (set, get) => ({
      comments: {},
      isLoading: false,

      fetchComments: async (postSite: string, postId: string) => {
        const key = `${postSite}:${postId}`
        set({ isLoading: true })

        try {
          const supabase = createClient()
          const { data, error } = await supabase
            .from("post_comments")
            .select(`
              *,
              profiles:user_id (username, profile_picture_url)
            `)
            .eq("post_site", postSite)
            .eq("post_id", postId)
            .eq("is_deleted", false)
            .order("created_at", { ascending: true })

          if (error) throw error

          const formattedComments = (data || []).map((c: any) => ({
            ...c,
            username: c.profiles?.username || "Anonymous",
            profile_picture_url: c.profiles?.profile_picture_url,
          }))

          set((state) => ({
            comments: {
              ...state.comments,
              [key]: formattedComments,
            },
          }))
        } catch (error) {
          console.error("Failed to fetch comments:", error)
        } finally {
          set({ isLoading: false })
        }
      },

      addComment: async (postSite: string, postId: string, content: string, userId: string, parentId?: string) => {
        try {
          const supabase = createClient()
          const { data, error } = await supabase
            .from("post_comments")
            .insert({
              user_id: userId,
              post_site: postSite,
              post_id: postId,
              content,
              parent_id: parentId || null,
            })
            .select(`
              *,
              profiles:user_id (username, profile_picture_url)
            `)
            .single()

          if (error) throw error

          const key = `${postSite}:${postId}`
          const newComment = {
            ...data,
            username: data.profiles?.username || "Anonymous",
            profile_picture_url: data.profiles?.profile_picture_url,
          }

          set((state) => ({
            comments: {
              ...state.comments,
              [key]: [...(state.comments[key] || []), newComment],
            },
          }))

          return true
        } catch (error) {
          console.error("Failed to add comment:", error)
          return false
        }
      },

      editComment: async (commentId: string, content: string) => {
        try {
          const supabase = createClient()
          const { error } = await supabase
            .from("post_comments")
            .update({ content, is_edited: true, updated_at: new Date().toISOString() })
            .eq("id", commentId)

          if (error) throw error

          // Update local state
          set((state) => {
            const newComments = { ...state.comments }
            for (const key in newComments) {
              newComments[key] = newComments[key].map((c) =>
                c.id === commentId ? { ...c, content, is_edited: true } : c,
              )
            }
            return { comments: newComments }
          })

          return true
        } catch (error) {
          console.error("Failed to edit comment:", error)
          return false
        }
      },

      deleteComment: async (commentId: string) => {
        try {
          const supabase = createClient()
          const { error } = await supabase.from("post_comments").update({ is_deleted: true }).eq("id", commentId)

          if (error) throw error

          // Remove from local state
          set((state) => {
            const newComments = { ...state.comments }
            for (const key in newComments) {
              newComments[key] = newComments[key].filter((c) => c.id !== commentId)
            }
            return { comments: newComments }
          })

          return true
        } catch (error) {
          console.error("Failed to delete comment:", error)
          return false
        }
      },

      voteComment: async (commentId: string, voteType: "up" | "down") => {
        try {
          const supabase = createClient()
          const field = voteType === "up" ? "upvotes" : "downvotes"

          // Get current value and increment
          const { data: current } = await supabase
            .from("post_comments")
            .select("upvotes, downvotes")
            .eq("id", commentId)
            .single()

          if (!current) return false

          const currentValue = voteType === "up" ? (current.upvotes ?? 0) : (current.downvotes ?? 0)
          const updates = voteType === "up" ? { upvotes: currentValue + 1 } : { downvotes: currentValue + 1 }

          const { error } = await supabase
            .from("post_comments")
            .update(updates)
            .eq("id", commentId)

          if (error) throw error

          // Update local state
          set((state) => {
            const newComments = { ...state.comments }
            for (const key in newComments) {
              newComments[key] = newComments[key].map((c) =>
                c.id === commentId ? { ...c, [field]: ((c[field as keyof Comment] as number) || 0) + 1 } : c,
              )
            }
            return { comments: newComments }
          })

          return true
        } catch (error) {
          console.error("Failed to vote:", error)
          return false
        }
      },

      getComments: (postSite: string, postId: string) => {
        const key = `${postSite}:${postId}`
        return get().comments[key] || []
      },
    }),
    {
      name: "booru-comments-db",
      partialize: () => ({}), // Don't persist, always fetch from DB
    },
  ),
)
